import { useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'

import { Banner, Card } from '../../components/ui.tsx'
import { asAuthError, post } from '../../lib/api.ts'
import { useCountryOptions } from '../../lib/countries.ts'
import { AuthCard, AuthHeading } from './AuthCard.tsx'
import { useAuthProviders } from './GoogleButton.tsx'
import { MIN_AGE, MAX_NAME_LENGTH, waitlistProblem, type WaitlistEntry } from '../../../shared/waitlist.ts'

/**
 * Stands in front of sign-up while accounts are closed.
 *
 * The screens behind it are kept, not deleted — opening accounts is one env var on the
 * server (`KELLYSTAKE_ACCOUNTS_OPEN=1`), and the server refuses their routes independently
 * of what renders here. While closed they send people to `/waitlist`, so the waitlist has
 * one address and a sign-up URL never quietly turns out to be a form that works. Nothing
 * renders until the answer is known, so a closed deployment never flashes the form.
 *
 * Sign-in is not behind this gate: it stays reachable, unlinked, for the accounts the
 * server has granted early access. See `Login`.
 */
export function AccountsGate({ children }: { children: ReactNode }) {
  const providers = useAuthProviders()
  if (!providers) return null
  return providers.accountsOpen ? <>{children}</> : <Navigate to="/waitlist" replace />
}

/**
 * The other side of the same switch: `/waitlist` exists only while accounts are closed.
 * Once they open, anyone arriving on an old link is sent to sign up instead.
 */
export function WaitlistGate({ children }: { children: ReactNode }) {
  const providers = useAuthProviders()
  if (!providers) return null
  return providers.accountsOpen ? <Navigate to="/signup" replace /> : <>{children}</>
}

const EMPTY: WaitlistEntry = { fullName: '', email: '', dateOfBirth: '', country: '' }

/** Join the waitlist. Every field is required, and the page says why before it asks. */
export function Waitlist() {
  const [entry, setEntry] = useState<WaitlistEntry>(EMPTY)
  const [busy, setBusy] = useState(false)
  const [joined, setJoined] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const countries = useCountryOptions()

  // The latest birth date that is still 18 today — the picker opens near the right decade
  // and cannot offer a date the server will refuse.
  const latestBirthDate = useMemo(() => {
    const d = new Date()
    d.setFullYear(d.getFullYear() - MIN_AGE)
    return d.toISOString().slice(0, 10)
  }, [])

  const set = (field: keyof WaitlistEntry) => (value: string) => setEntry((e) => ({ ...e, [field]: value }))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    const problem = waitlistProblem(entry)
    if (problem) return setErr(problem)

    setBusy(true)
    setErr(null)
    try {
      await post('/auth/waitlist', entry)
      setJoined(true)
    } catch (error) {
      setErr(asAuthError(error).error)
    } finally {
      setBusy(false)
    }
  }

  if (joined) {
    return (
      <AuthCard width={440}>
        <AuthHeading title="You're on the list" />
        <Card pad>
          <Banner tone="success" icon="✓" title="Thanks for joining" flush>
            We'll email <span className="mono">{entry.email}</span> when your place is ready. That is the
            only email we'll send about it.
          </Banner>
        </Card>
      </AuthCard>
    )
  }

  return (
    <AuthCard width={440}>
      <AuthHeading title="Join the waitlist" sub="KellyStake is opening to new accounts in stages." />

      <Banner tone="incident" title="Why we ask for these details">
        <p style={{ margin: 0, textAlign: 'justify' }}>
          Betting is regulated, and who we are allowed to serve depends on your age and the country you live in.
          You must be {MIN_AGE} or over.
        </p>
      </Banner>

      <Card pad>
        <form onSubmit={submit} noValidate>
          <div className="field">
            <label htmlFor="wl-name">Full name</label>
            <input
              className="input"
              id="wl-name"
              autoComplete="name"
              autoFocus
              required
              maxLength={MAX_NAME_LENGTH}
              value={entry.fullName}
              onChange={(e) => set('fullName')(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="wl-email">Email</label>
            <input
              className="input"
              type="email"
              id="wl-email"
              placeholder="you@example.com"
              autoComplete="email"
              required
              value={entry.email}
              onChange={(e) => set('email')(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="wl-dob">Date of birth</label>
            <input
              className="input"
              type="date"
              id="wl-dob"
              autoComplete="bday"
              required
              max={latestBirthDate}
              value={entry.dateOfBirth}
              onChange={(e) => set('dateOfBirth')(e.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="wl-country">Country of residence</label>
            <select
              className="select"
              id="wl-country"
              autoComplete="country"
              required
              value={entry.country}
              onChange={(e) => set('country')(e.target.value)}
            >
              <option value="" disabled>
                Choose a country
              </option>
              {countries.map(({ code, name }) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {err && (
            <div className="field" style={{ marginBottom: 12 }}>
              <span className="help warn" role="alert">
                {err}
              </span>
            </div>
          )}

          <button type="submit" className="btn primary block lg" disabled={busy}>
            {busy ? 'Joining…' : 'Join the waitlist'}
          </button>
        </form>
      </Card>

      <p className="u-center u-faint u-sm" style={{ marginTop: 18 }}>
        We use these details only to check eligibility and to tell you when your place is ready.
      </p>
    </AuthCard>
  )
}
